import { Component, effect, inject, viewChild } from '@angular/core';
import { IChatSession, IFullDetailChatSession } from "../../interfaces/i-chat-session";
import {
    CancelEvent,
    DataBindingDirective,
    GridComponent,
    KENDO_GRID
} from "@progress/kendo-angular-grid";
import { KENDO_GRID_PAGE_SIZES } from "../../constants/api.constant";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { fileExcelIcon, pencilIcon, plusIcon, SVGIcon, trashIcon } from "@progress/kendo-svg-icons";
import { process } from "@progress/kendo-data-query";
import { ChatSessionService } from "../ai-chat/services/chat-session.service";
import { CommonModule } from "@angular/common";
import { InputsModule, TextBoxComponent } from "@progress/kendo-angular-inputs";
import { LabelModule } from "@progress/kendo-angular-label";
import { KENDO_PAGER } from "@progress/kendo-angular-pager";
import { AgentService } from "../agent/services/agent.service";
import { ButtonComponent } from "@progress/kendo-angular-buttons";
import { MatTooltip } from "@angular/material/tooltip";
import { DialogConfirmComponent } from "../../shared/dialog-confirm/dialog-confirm.component";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { SpinnerService } from "../../shared/spinner/spinner.service";
import { AlertService } from "../../shared/alert/alert.service";
import { ApiService } from "../../services/api.service";
import { SensitivityLabelService } from "../../services/sensitivity-label.service";
import { UiLayoutService } from "../../ui-layout/services/ui-layout.service";
import { ChatMessageService } from "../ai-chat/services/chat-message.service";
import { AlertEnum } from "../../shared/alert/alert.enum";
import { ActionType } from "../../types/app.types";
import { SessionManagerComponent } from "../ai-chat/session-manager/session-manager.component";
import { ISensitivityLabel, ISensitivityLabelExpanded } from "../../interfaces/i-sensitivity-label";
import { mapLegacySensitivityLabel, toISensitivityLabel } from "../../utilities/sensitivity-label.utils";
import { NavItemLabelIds } from "../../enums/api.enum";
import { IManageSession } from "./interfaces/i-manage-session";
import { Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { InfoButtonTooltipComponent } from '../../shared/info-button-tooltip/info-button-tooltip.component';
import { IOpenAiModel } from "../../interfaces/i-open-ai-model";
import { AiChatService } from "../ai-chat/services/ai-chat.service";
import { SessionStatus } from '../../enums/session-status.enum';

@Component({
    selector: 'app-manage-sessions',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextBoxComponent,
    InputsModule,
    LabelModule,
    KENDO_GRID,
    KENDO_PAGER,
    ButtonComponent,
    MatTooltip,
    MatButtonModule,
    MatIconModule,
    InfoButtonTooltipComponent,
  ],
    templateUrl: './manage-sessions.component.html',
    styleUrl: './manage-sessions.component.scss'
})
export class ManageSessionsComponent {
    dataBinding = viewChild<DataBindingDirective>(DataBindingDirective);
    gridData: IManageSession[] = [];
    gridView: IManageSession[] = [];
    pageSize = 10;
    pageSizes = KENDO_GRID_PAGE_SIZES;
    total = 0;
    formGroup: FormGroup | undefined;
    editedRowIndex: number | undefined;
    editColumnWidth!: number;  // Width of the edit column. This is used to adjust the width of the column when editing
    dateColumnWidth!: number;  // Width of the date columns. This is used to adjust the width of the column when editing
    // END Kendo UI Grid configuration
    expandedDetailKeys: string[] = [];

    protected readonly pencilIcon: SVGIcon = pencilIcon;
    protected readonly trashIcon: SVGIcon = trashIcon;
    protected readonly plusIcon: SVGIcon = plusIcon;
    protected readonly SessionStatus = SessionStatus;
    protected readonly actionButtonTooltipText = 'Session is in progress. Please wait for the Session to finish and try again later.';
    readonly dialog = inject(MatDialog);

    #chatSessionService = inject(ChatSessionService);
    #agentService = inject(AgentService);
    #spinnerService = inject(SpinnerService);
    #alertService = inject(AlertService);
    #apiService = inject(ApiService);
    #uiLayoutService = inject(UiLayoutService);
    #chatMessageService = inject(ChatMessageService);
    #router = inject(Router);
    #aiChatService = inject(AiChatService);
    #sensitivityLabelService = inject(SensitivityLabelService);

    /** Whether the sensitivity-label column is shown; hidden when the feature is disabled. */
    readonly slEnabled = this.#sensitivityLabelService.enabled;

    constructor() {
        this.#setColumnWidths();
        this.#getFullDetailSessions();
        this.#syncSessionStatus();
    }

    #syncSessionStatus(): void {
        effect(() => {
            const sessions = this.#chatSessionService.chatSessions();
            if (!sessions || this.gridData.length === 0) return;

            // Build a Map once for O(1) lookups instead of find() inside map() — O(n+m) vs O(n×m)
            const statusMap = new Map(sessions.map(s => [s.id, s.lastStatus]));

            let hasChanges = false;
            const updatedGridData = this.gridData.map(item => {
                const newStatus = statusMap.get(item.id);
                if (newStatus === item.details.lastStatus) return item;
                hasChanges = true;
                return { ...item, details: { ...item.details, lastStatus: newStatus } };
            });

            if (!hasChanges) return;

            this.gridData = updatedGridData;
            // Re-derive gridView from the updated gridData to avoid a second traversal.
            // gridView is either gridData (unfiltered) or a filtered subset, so map the
            // updated items back using the same statusMap rather than re-running updateItems.
            this.gridView = this.gridView.map(item => {
                const newStatus = statusMap.get(item.id);
                if (newStatus === item.details.lastStatus) return item;
                return { ...item, details: { ...item.details, lastStatus: newStatus } };
            });
        });
    }

    /**
     * Filter the grid data based on the input value
     */
    onFilter(value: Event): void {
        this.gridView = process(this.gridData, {
            filter: {
            logic: "or",
            filters: [
                {
                field: "details.label",
                operator: "contains",
                value: value,
                },
                {
                field: "sensitivityLabelsList",
                operator: "contains",
                value: value,
                },
                {
                field: "modifiedDate",
                operator: "contains",
                value: value,
                },
                {
                field: "createdDate",
                operator: "contains",
                value: value,
                },
                {
                field: "details.model.model",
                operator: "contains",
                value: value,
                }
            ],
            },
        }).data;

        const dataBindingInstance = this.dataBinding();
        if (dataBindingInstance) {
            dataBindingInstance.skip = 0;
        }
        this.total = this.gridView.length;
    }

    expandDetailsBy = (dataItem: IManageSession): string => {
        return dataItem.id;
    };

    editHandler(session: any): void {
        // Make the selected session the current session
        this.#chatSessionService.selectedSessionId.set(session.id);
        this.#openCreateOrEditSessionDialog("edit");
    }

    cancelHandler(args: CancelEvent): void {
        // close the editor for the given row
        this.#closeEditor(args.sender, args.rowIndex);
    }

    openDeleteDialog(session: IFullDetailChatSession): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
            message: `Are you sure you want to delete your session "${session.details.label}"?`,
            buttonLabel: 'Delete',
            title: 'Confirm',
            tertiaryButtonLabel: '',
            showTertiaryButton: false,
            showSecondaryButton: true
        };
        dialogConfig.disableClose = true;

        const dialogRef = this.dialog.open(DialogConfirmComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.#deleteSession(session.details);
            }
        });
    }

    openHandler(session: any): void {
        const navItem = this.#uiLayoutService.navItems().find(item => item.id === session.id);
        this.#chatSessionService.selectedSessionId.set(session.id);
        this.#chatMessageService.messages.set(this.#chatSessionService.selectedSession()?.messages);
        this.#uiLayoutService.setSelectedNavItem(session.id);
        this.#router.navigateByUrl(navItem?.path!);
    }

    createNewSession(): void {
        this.#openCreateOrEditSessionDialog("create");
    }

    onDetailExpand(event: any): void {
        const agentIds = event.dataItem.details.agents;
        const agents = event.dataItem.agents;

        // Check if the agents are already loaded for this session.  If not, fetch them.
        if (!agents || agents.length === 0) {
            this.#getSessionAgents(agentIds, event.dataItem.id);
        }
    }

    #closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
        // close the editor
        grid.closeRow(rowIndex);
        // reset the helpers
        this.editedRowIndex = undefined;
        this.formGroup = undefined;
        this.#setColumnWidths();
    }

    #setColumnWidths() {
        this.editColumnWidth = 120;
        this.dateColumnWidth = 150;
    }

    #deleteSession(session: IChatSession) {
        this.#spinnerService.show();
        // First we need to set the selected session ID to the one being deleted.
        this.#chatSessionService.selectedSessionId.set(session.id);
        this.#apiService.deleteSession(session.id).subscribe({
            next: response => {
                const currentSessions = this.#chatSessionService.chatSessions();
                // First remove the deleted session from the nav menu
                const items = this.#uiLayoutService.navItems().filter(item => item.id !== this.#chatSessionService.selectedSessionId());
                this.#uiLayoutService.navItems.set(items);
                // Check if there are any sessions left, if so, set the first one as the selected session and update the URL.
                // If not, clear the messages and the selected session. Then navigate to the Session Page.
                if (currentSessions && currentSessions.length > 0) {
                    // First check if the session being deleted is the selected session.
                    // We need to set a new selected session if it is.
                    if (session.id === this.#chatSessionService.selectedSessionId()) {
                        // Set the current session.  Use the first in the list.
                        this.#chatSessionService.selectedSessionId.set(currentSessions[0].id);
                        this.#chatMessageService.messages.set(currentSessions[0].messages);
                    }
                } else {
                    // If there are no sessions left, clear the messages and the selected session.
                    this.#chatMessageService.messages.set([]);
                    this.#chatSessionService.selectedSessionId.set('');
                }

                // Now we need to update the grid data.
                this.gridData = this.gridData.filter((s: IFullDetailChatSession) => s.id !== session.id);
                this.gridView = this.gridData;
                this.total = this.gridView.length;

                this.#spinnerService.hide();
                this.#alertService.showAlert({
                    message: `Deleted session "${session.label}" successfully.`,
                    type: AlertEnum.success
                });
            },
            error: err => {
                this.#alertService.showAlert({
                    message: `Failed to delete session "${session.label}"`,
                    type: AlertEnum.error
                });

                this.#spinnerService.hide();
            }
        });
    }

    #openCreateOrEditSessionDialog(action: ActionType) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.width = '100vw';
        // dialogConfig.height = '100vh';
        dialogConfig.maxWidth = '100vw';
        // dialogConfig.maxHeight = '100vh';
        dialogConfig.data = { action: action };
        dialogConfig.autoFocus = '#mat-dialog-title-0';

        const dialogRef = this.dialog.open(SessionManagerComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(
            async (response: {
                id: string;
                action: ActionType;
                sensitivityLabel: ISensitivityLabelExpanded;
                temperature: number;
                topP: number;
                model: IOpenAiModel;
                isLabelChanged: boolean;
                sessionLabel: string;
                agents: string[];
                sessionData: IFullDetailChatSession;
            }) => {
                if (response.id) {
                    if (response.action === 'edit') {
                        const chatSessions = this.#chatSessionService.chatSessions();
                        // For some reason, when the chatSessions are updated, the selected session is not updated.
                        // So we need to set the selected session ID to empty and then set it back to the current session ID.
                        // This will trigger the selectedSession() computed to update the session.
                        this.#chatSessionService.selectedSessionId.set(response.id);
                        const currentSession = this.#chatSessionService.selectedSession();

                        if (currentSession) {
                            const updatedSession: IChatSession = {
                                ...currentSession,
                                label: response.sessionLabel,
                                agents: response.agents,
                                sensitivityLabels: [toISensitivityLabel(response.sensitivityLabel)],
                                model: response.model,
                                temperature: response.temperature,
                                topP: response.topP,
                            };

                            if (chatSessions) {
                                this.#chatSessionService.chatSessions.set(
                                    chatSessions.map((session) =>
                                        session.id === updatedSession.id
                                            ? updatedSession
                                            : session
                                    )
                                );
                            }
                        }

                        // Track changes for sensitivity label, agents, model, temperature, and topP
                        const updatedFields = [];

                        // Check if sensitivity label was changed (compare canonical GUIDs via legacy mapper)
                        if (
                            currentSession &&
                            currentSession.sensitivityLabels &&
                            mapLegacySensitivityLabel(currentSession.sensitivityLabels[0])?.id !== response.sensitivityLabel.id
                        ) {
                            updatedFields.push(
                                `- Sensitivity Label: from ${this.#agentService.getSensitivityLabelString(
                                    currentSession.sensitivityLabels[0]
                                )} to ${this.#agentService.getSensitivityLabelString(
                                    response.sensitivityLabel
                                )}`
                            );

                            // Handle sensitivity label mismatch
                            this.#aiChatService.handleSensitivityLabelMismatch(currentSession.id, response.sensitivityLabel.id);
                        }

                        // Check if agents were changed and add to updatedFields if they were
                        if (currentSession && currentSession.agents) {
                            const agentChanges = await this.#aiChatService.formatAgentChanges(
                                currentSession.agents,
                                response.agents
                            );
                            if (agentChanges) {
                                updatedFields.push(agentChanges);
                            }
                        }

                        // Check for model, temperature, and topP changes
                        const aiConfigChanges = this.#aiChatService.handleAiConfigMismatch(
                            currentSession,
                            response.model,  // Use the model from the dialog response
                            response.temperature,  // Use the temperature from the dialog response
                            response.topP  // Use the topP from the dialog response
                        );

                        // Add any AI config changes to the updatedFields array
                        updatedFields.push(...aiConfigChanges);

                        // Create system message for configuration changes if needed
                        const changesMade = await this.#aiChatService.createConfigChangeSystemMessage(currentSession, updatedFields, response.id);

                        // If no changes were made or no system message was created, show a simple success alert
                        if (!changesMade) {
                            this.#alertService.showAlert({
                                message: `Session ${response.sessionLabel} has been saved.`,
                                type: AlertEnum.success,
                            });
                        }
                    } else {
                        this.#alertService.showAlert({
                            message: `Session ${response.sessionLabel} has been saved.`,
                            type: AlertEnum.success,
                        });
                        // NOTE: For some reason, the dialog is not closing when the action is 'create' and there are no other sessions.
                        // This is a workaround to close the dialog manually.
                        this.dialog.closeAll();
                    }

                    const chatSessions = this.#chatSessionService.chatSessions();
                    const currentSession = this.#chatSessionService.selectedSession();

                    const items = this.#uiLayoutService.generateUpdatedNavItems(response.id, response.action, false, chatSessions!, currentSession!);
                    this.#uiLayoutService.updateAndSortChatSessionsInLeftNav(response.id, items);
                    this.#uiLayoutService.setSelectedNavItem(NavItemLabelIds.ManageSessions);
                    this.#updateGridData(response.sessionData, response.action);
                    if (currentSession) {
                        // Check if the row detail is expanded for the current session.  If so, we need to update the agents in the row detail.
                        const isSessionRowExpanded = this.expandedDetailKeys.includes(currentSession.id);
                        if (isSessionRowExpanded && (!currentSession.agents || currentSession.agents.length === 0)) {
                            this.#getSessionAgents(currentSession.agents, currentSession.id);
                        }
                    }
                }
            }
        );
    }

    #getFullDetailSessions() {
        this.#spinnerService.show();
        this.#apiService.getFullDetailSessions().subscribe({
            next: sessions => {
                this.#spinnerService.hide();
                if (sessions) {
                    const sessionsData: IManageSession[] = sessions.map((item: IFullDetailChatSession) => {
                        let sensitivityLabelsList = 'No sensitivity label assigned'; // Set default value
                        if (Object.hasOwn(item.details, 'sensitivityLabels') && item.details.sensitivityLabels!.length > 0) {
                          sensitivityLabelsList = item.details.sensitivityLabels!
                            .map((sensitivityLabel: ISensitivityLabel) => this.#agentService.getSensitivityLabelString(sensitivityLabel))
                            .join('; ');
                        }
                        return {
                            ...item,
                            sensitivityLabelsList
                        };
                    });

                    this.gridData = sessionsData;
                    this.gridView = sessionsData;
                    this.total = this.gridView.length;
                }
            },
            error: (error) => {
                this.#spinnerService.hide();
                console.error('Error getting sessions', error);
            },
        });
    }

    #updateGridData(updatedSession: IFullDetailChatSession, action: ActionType) {
        // Update the grid data with the session data that has sensitivityLabelsList
        let gridSession: IManageSession = {
            ...updatedSession,
            sensitivityLabelsList: this.#agentService.getSensitivityLabelString(updatedSession.details.sensitivityLabels![0])
        };

        if (action === 'create') {
            this.gridData.unshift(gridSession);
        } else {
            this.gridData = this.gridData.map(session => {
                if (session.id === gridSession.id) {
                    return gridSession;
                }
                return session;
            });
        }

        // Create a new array reference to trigger change detection else the grid in the UI will not update
        this.gridData = [ ...this.gridData ];
        this.gridView = this.gridData;
        this.total = this.gridView.length;
    }

    #getSessionAgents(agentIds: string[], sessionId: string) {
        if (agentIds === undefined || agentIds.length === 0) {
            // No agents to fetch, so we can skip the API call
            return;
        }

        this.#spinnerService.show();
        this.#apiService.getAgentsByIds(agentIds).subscribe({
            next: agents => {
                // Find the record in gridData that matches the sessionId
                const record = this.gridView.find(item => item.id === sessionId);
                if (record) {
                    // Update the agents property of the found record
                    record.agents = agents;
                }
                this.#spinnerService.hide();
            },
            error: error => {
                console.error('Error getting agents', error);
                this.#spinnerService.hide();
            }
        });
    }

  protected readonly excelSVG = fileExcelIcon;
}
